'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import OutlinedChip from '@umodeapporg/ui/components/UI/Chips/OutlinedChip';
import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Notice } from '@/components/surface/Notice';
import { SURFACE_CARD, SURFACE_INSET } from '@/components/surface/surfaces';
import { IProposta } from '@/domain/agente/interfaces/agente';
import { cn } from '@/lib/utils';

interface IPropostaCardProps {
  proposta: IProposta;
  onConfirmar: (id: string) => Promise<void>;
  loadingId: string | null;
}

/**
 * One pending action proposal: summary, optional warning, before/after rows and the
 * confirm/discard pair. The rows are a key/value list, not a table: the card lives in a chat
 * bubble, where three fixed columns squeeze every value into a sliver.
 */
export function PropostaCard({ proposta, onConfirmar, loadingId }: IPropostaCardProps) {
  const { t } = useTranslation('common');
  const [descartada, setDescartada] = useState(false);

  if (descartada) {
    return null;
  }

  const confirmando = loadingId === proposta.id;

  const handleConfirmar = async (): Promise<void> => {
    await onConfirmar(proposta.id);
  };

  return (
    <div className={cn(SURFACE_CARD, 'flex animate-fade-in-up flex-col gap-3 p-4')}>
      <div className="flex items-center">
        <OutlinedChip
          label={t('agente.proposta.titulo')}
          tone="purple"
          variant="soft"
          size="xs"
          chipIcon="auto_awesome"
          preserveLabelCase
        />
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-foreground">{proposta.titulo}</p>
        <p className="text-sm text-foreground-muted">{proposta.resumo}</p>
      </div>

      {proposta.aviso !== null && (
        <Notice tone="warning" size="sm" title={t('agente.proposta.aviso')}>
          {proposta.aviso}
        </Notice>
      )}

      {proposta.linhas.length > 0 && (
        <dl className={cn(SURFACE_INSET, 'flex flex-col divide-y divide-border-subtle text-sm')}>
          {proposta.linhas.map((linha) => (
            <div key={linha.rotulo} className="flex flex-col gap-0.5 px-3 py-2">
              <dt className="break-words text-xs font-medium text-foreground-muted">{linha.rotulo}</dt>
              <dd className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 break-words">
                <span className="text-foreground-subtle line-through">
                  <span className="sr-only">{`${t('agente.proposta.antes')}: `}</span>
                  {linha.antes}
                </span>
                <MaterialSymbol
                  icon="arrow_forward"
                  size={14}
                  className="self-center text-foreground-faint"
                  aria-hidden
                />
                <span className="font-medium text-success-emphasis">
                  <span className="sr-only">{`${t('agente.proposta.depois')}: `}</span>
                  {linha.depois}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex flex-wrap gap-2">
        <ControlledButton size="sm" disabled={confirmando} onClick={handleConfirmar}>
          {confirmando && <LoadingSpinner size={3} color="brand-fg" />}
          {t('agente.proposta.confirmar')}
        </ControlledButton>
        <ControlledButton size="sm" variant="outlined" tone="neutral" onClick={() => setDescartada(true)}>
          {t('agente.proposta.descartar')}
        </ControlledButton>
      </div>
    </div>
  );
}
